/* eslint-disable */
import { Amplify } from 'aws-amplify';
import { getCurrentUser, signInWithRedirect } from '@aws-amplify/auth';
import { fetchAuthSession, signIn } from 'aws-amplify/auth';
import { ApplicationState, useAppStore } from '../store';
import { Platform } from 'react-native';
import { TenantFeature, TenantTuple } from '../types/AuthTypes';
import { signOutUser, deleteAccount } from '../helpers/AuthHelper';
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../types/ApiResponse';
import { Config } from '../constants/Config';
import { DataSharingTenant } from '../types/DataSharingTenant';
import { ProviderName } from '../types/CognitoUser';
import { UserAttributes } from '../types/UserAttributes';
import UserService from '../../api/user';
import { ValidateError } from '../../core';
import { addRawAnalyticsLog } from '../helpers/AddRawAnalyticsLog';
import { getValue, setValue } from 'src/api/storage';
import { useNotifications } from './useNotifications';
import { DataSharingRequest } from '../types/DataSharingRequest';
import { Hub } from '@aws-amplify/core';

export enum SocialVariant {
  googauth = 'googauth',
  appleAuth = 'appleAuth'
}

interface UseUser {
  signInByEmail: (email: string, password: string) => Promise<any>;
  signInBySocial: (type: SocialVariant) => Promise<any>;
  signOut: (global?: boolean) => Promise<any>;
  getToken: () => Promise<string>;
  deleteUserAccount: () => Promise<boolean>;
  getProviderName: () => Promise<ProviderName | null>;
}

export function useUser(): UseUser {
  const signInByEmail = async (email: string, password: string): Promise<any> => {
    return signIn({ username: email, password });
  };

  const signOut = useCallback(async (global?: boolean) => {
    return signOutUser(global);
  }, []);

  const getToken = useCallback(async () => {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || '';
  }, []);

  const deleteUserAccount = async (): Promise<boolean> => {
    try {
      await deleteAccount();
      return true;
    } catch (error) {
      addRawAnalyticsLog('Error', { file: 'useUser.tsx', function: 'deleteUserAccount', data: error });
      return false;
    }
  };

  const signInBySocial = (type: SocialVariant): Promise<any> => {
    if (type === SocialVariant.appleAuth) {
      return signInWithRedirect({ provider: 'Apple' });
    } else if (type === SocialVariant.googauth) {
      return signInWithRedirect({ provider: 'Google' });
    }
    return Promise.reject();
  };

  const getProviderName = useCallback(async (): Promise<ProviderName | null> => {
    type Identity = { providerName: string };
    type IdTokenPayload = {
      identities?: Identity[];
    };
    const { tokens } = await fetchAuthSession();
    const payload = tokens?.idToken?.payload as IdTokenPayload | undefined;

    if (payload?.identities && Array.isArray(payload.identities) && payload.identities[0]?.providerName) {
      const identityProvider = payload.identities[0].providerName;
      return identityProvider as ProviderName;
    }

    return null;
  }, []);

  return { signInByEmail, signOut, getToken, deleteUserAccount, signInBySocial, getProviderName };
}

var configured = false;

const initAmplify = (): boolean => {
  if (configured == true) {
    return true;
  }

  // const urlOpener = async (url: any, redirectUrl: any) => {
  //   await InAppBrowser.isAvailable();
  //   const ephemeralWebSession = String(url).indexOf('logout') > 0;
  //   const { type, url: newUrl } = (await InAppBrowser.openAuth(url, redirectUrl, {
  //     showTitle: false,
  //     enableUrlBarHiding: true,
  //     enableDefaultShare: false,
  //     ephemeralWebSession: ephemeralWebSession
  //   })) as any;

  //   if (type == 'cancel') {
  //     InAppBrowser.close();
  //   } else if (type === 'success') {
  //     Linking.openURL(newUrl);
  //   }
  // };

  try {
    Amplify.configure({
      Auth: {
        Cognito: {
          identityPoolId: Config.COGNITO_IDENTITY_POOL_ID,
          userPoolId: Config.COGNITO_USER_POOL_ID,
          userPoolClientId: Config.COGNITO_USER_POOL_WEB_CLIENT_ID,
          loginWith: {
            oauth: {
              domain: Config.COGNITO_DOMAIN,
              scopes: ['aws.cognito.signin.user.admin', 'email', 'profile', 'openid', 'phone'],
              redirectSignIn: [
                Platform.OS === 'web' ? Config.COGNITO_REDIRECT_SIGNIN_WEB : Config.COGNITO_REDIRECT_SIGNIN
              ],
              redirectSignOut: [
                Platform.OS === 'web' ? Config.COGNITO_REDIRECT_SIGNOUT_WEB : Config.COGNITO_REDIRECT_SIGNOUT
              ],
              responseType: 'code',
              providers: ['Google', 'Apple']
            }
          }
        }
      },
      Storage: {
        S3: {
          bucket: Config.USER_FILES_S3_BUCKET,
          region: Config.AWS_REGION
        }
      }
    });

    configured = true;
  } catch (error) {
    console.error(`initAmplify: ${JSON.stringify(error)}`);
    return false;
  }

  return true;
};

export const validUserInfoResponse = (data: any): UserAttributes => {
  for (const i of Object.keys(data)) {
    if (i !== 'picture' && i !== 'birthdate' && i !== 'createdOn' && data[i] == null) {
      throw Error('Null value in user info');
    }
  }

  return {
    name: data.name,
    nickname: data.nickname,
    email: data.email,
    gender: data.gender,
    birthdate: data.birthdate,
    picture: data.picture,
    sub: data.cognitoId,
    userId: data.id
  };
};

export const useUserInitializer = (): boolean => {
  const [isLoginModuleInit, setIsLoginModuleInit] = useState<boolean>(false);
  const isAppInit = useAppStore((state: ApplicationState) => state.isAppPreInit);

  useEffect(() => {
    if (isAppInit) {
      initAmplify();
      setIsLoginModuleInit(true);
    }
  }, [isAppInit]);

  return isLoginModuleInit;
};

export const useUserLoginInitializer = (): boolean => {
  const isAppInit = useAppStore((state: ApplicationState) => state.isAppPreInit);
  const isAppInitComplete = useAppStore((state: ApplicationState) => state.isAppInitComplete);
  const setUser = useAppStore((state: ApplicationState) => state.setUser);
  const setUserLogged = useAppStore((state: ApplicationState) => state.setUserLogged);
  const setAuthLoading = useAppStore((state: ApplicationState) => state.setAuthLoading);
  const changeUserAttributes = useAppStore((state: ApplicationState) => state.changeUserAttributes);
  const clearUser = useAppStore((state: ApplicationState) => state.clearUser);
  const [isLoginModuleInit, setIsLoginModuleInit] = useState<boolean>(false);
  const setAutoLogin = useAppStore((state: ApplicationState) => state.setAutoLogin);
  const { removeExternalUserId } = useNotifications();

  const loadUserData = useCallback(async () => {
    const isLoadingUserData = await getValue('isLoadingUserData');
    if (isLoadingUserData == 'true') {
      return false;
    }

    await setValue('isLoadingUserData', 'true');

    // Note: In Amplify v6, user attributes are fetched differently
    const { tokens } = await fetchAuthSession();
    if (!tokens?.idToken) {
      return false;
    }
    const attributes = tokens?.idToken?.payload ?? {};

    let name = '';

    if (typeof attributes.name === 'string' && attributes.name.length > 0) {
      name = attributes.name;
    } else if (typeof attributes.given_name === 'string' && typeof attributes.family_name === 'string') {
      name = `${attributes.given_name} ${attributes.family_name}`;
    }

    let nickname = '';

    if (typeof attributes.nickname === 'string' && attributes.nickname.length > 0) {
      nickname = attributes.nickname;
    } else {
      nickname = name;
    }

    const userInfo: UserAttributes = {
      name,
      nickname,
      email: typeof attributes.email === 'string' ? attributes.email : undefined,
      gender: typeof attributes.gender === 'string' ? attributes.gender : undefined,
      birthdate: typeof attributes.birthdate === 'string' ? attributes.birthdate : undefined,
      sub: typeof attributes.sub === 'string' ? attributes.sub : '',
      height: undefined,
      weight: undefined,
      smoker: undefined,
      bloodpressureMedication: undefined,
      hypertension: undefined,
      diabetic: undefined,
      activityLevel: undefined
    };

    let savedUser: UserAttributes = userInfo;

    try {
      const response = await UserService.getUserInfo().catch(() => UserService.addUser(userInfo));
      savedUser = validUserInfoResponse(response);
      changeUserAttributes({
        ...savedUser,
        ...userInfo,
        name: userInfo.name != undefined && userInfo.name.length > 0 ? userInfo.name : savedUser.name,
        gender: response?.gender ? response?.gender : undefined,
        birthdate: response?.birthdate ? response?.birthdate : undefined
      });
      setUser(savedUser.userId ?? '', attributes.sub ?? '', String(tokens?.idToken), String(attributes.email ?? ''));
      await setValue('isLoadingUserData', 'false');
      return true;
    } catch (error) {
      console.error('getUserInfo failed:', error);
      await setValue('isLoadingUserData', 'false');
      return false;
    }
  }, [changeUserAttributes, setUser]);

  const processSignInUp = useCallback(async () => {
    await loadUserData()
      .then((success) => {
        if (success == true) {
          setUserLogged(true);
        }
      })
      .finally(async () => {
        await setValue('isLoadingUserData', 'false');
      });
  }, [setUserLogged, loadUserData]);

  const processSignOut = useCallback(async () => {
    try {
      await removeExternalUserId();
    } catch (error) {
      console.error(`removeExternalUserId: ${JSON.stringify(error)}`);
    }

    await setValue('tenantKey', '');
    clearUser();
  }, [clearUser, removeExternalUserId]);

  useEffect(() => {
    if (isAppInitComplete) {
      setAuthLoading(false);
      setAutoLogin(false);
    }
  }, [isAppInitComplete, setAuthLoading, setAutoLogin]);

  useEffect(() => {
    if (isAppInit) {
      initAmplify();

      getCurrentUser()
        .then(() => {
          return loadUserData().then((success) => {
            if (success == true) {
              setAutoLogin(true);
              setUserLogged(true);
            }
          });
        })
        .catch((error) => {
          console.warn(`getCurrentUser: ${JSON.stringify(error)}`);
        })
        .finally(async () => {
          setIsLoginModuleInit(true);
          await setValue('isLoadingUserData', 'false');
        });

      const unsubscribe = Hub.listen('auth', ({ payload: { event } }) => {
        switch (event) {
          case 'signedIn':
            processSignInUp();
            break;
          case 'signedOut':
            processSignOut();
            break;
          case 'signInWithRedirect_failure':
            console.error('Redirect sign-in failed');
            processSignOut();
            break;
          case 'customOAuthState':
            break;
        }
      });

      return unsubscribe;
    }
  }, [
    isAppInit,
    clearUser,
    loadUserData,
    processSignInUp,
    processSignOut,
    setUserLogged,
    setAuthLoading,
    setAutoLogin
  ]);

  return isLoginModuleInit;
};

export const useIsUserLogged = (): boolean => {
  return useAppStore((state: ApplicationState) => state.isLogged);
};

export const useIsUserAutoLogin = (): boolean => {
  return useAppStore((state: ApplicationState) => state.isAutoLogin);
};

// export const useGetInternalUserId = (): string | undefined => {
//   return useAppStore((state: ApplicationState) => state.userId);
// };

export const useGetExternalUserId = (): string | undefined => {
  return useAppStore((state: ApplicationState) => state.externalUserId);
};

export const useGetUserEmail = (): string | undefined => {
  return useAppStore((state: ApplicationState) => state.email);
};

export const useGetUserAttributes = (): UserAttributes => {
  return useAppStore((state: ApplicationState) => state.userAttributes);
};

export const useChangeAttributes = (): ((data: UserAttributes) => void) => {
  return useAppStore((state: ApplicationState) => state.changeUserAttributes);
};

export const useGetTenantKey = (): string => {
  return useAppStore((state: ApplicationState) => state.tenantKey);
};

export const useGetTenants = (): TenantTuple[] => {
  return useAppStore((state: ApplicationState) => state.tenants);
};

export const useGetTenantFeatures = (): TenantFeature[] => {
  return useAppStore((state: ApplicationState) => state.tenantFeatures);
};

export const useGetIsAuthLoading = (): boolean => {
  return useAppStore((state: ApplicationState) => state.isAuthLoading);
};

export const useGetAuthError = (): ValidateError => {
  return useAppStore((state: ApplicationState) => state.authError);
};

export const useSetAuthLoading = (): ((loading: boolean) => void) => {
  return useAppStore((state: ApplicationState) => state.setAuthLoading);
};

export const useSetAuthError = (): ((error: ValidateError) => void) => {
  return useAppStore((state: ApplicationState) => state.setAuthError);
};

export const useGetUserId = () => {
  return useAppStore((state: ApplicationState) => state.internalUserId);
};

export const useGetDataSharingList = (): DataSharingTenant[] => {
  return useAppStore((state) => state.dataSharingList);
};

export const useSetDataSharingList = (): ((dataSharingList: DataSharingTenant[]) => void) => {
  return useAppStore((state) => state.setDataSharingList);
};

export const useSetDataSharingRequestList = (): ((dataSharingRequestList: DataSharingRequest[]) => void) => {
  return useAppStore((state) => state.setDataSharingRequestList);
};

export const useGetProfilePhotoEffect = () => {
  const setProfilePhoto = useAppStore((state) => state.setProfilePhoto);
  const [error, setError] = useState<any>();

  useEffect(() => {
    UserService.getProfilePhoto()
      .then((data) => {
        if (!data.length) {
          setProfilePhoto('');
        } else {
          setProfilePhoto(data);
        }
      })
      .catch((err: ApiError) => {
        // User has no profile picture
        setError(err.error);
        setProfilePhoto('');
      });
  }, [setProfilePhoto]);
  return [error];
};
